/**
 * <materials-section> — Materials page custom element.
 * Tabs (hash deep-linking) and per-card Swiper galleries with
 * Ajaxinate "load more". connectedCallback wires up; disconnectedCallback tears down,
 * so the theme editor can re-render the section without leaking listeners or instances.
 * Requires globals deferred before this file: jQuery ($), Swiper, Ajaxinate.
 */
class MaterialsSection extends HTMLElement {
  connectedCallback() {
    // The globals above are deferred and may load after this element upgrades; wait if needed.
    if (this.dependenciesReady()) this.setup();
    else document.addEventListener('DOMContentLoaded', () => this.isConnected && this.setup(), { once: true });
  }

  disconnectedCallback() {
    if (!this.listeners) return;
    this.listeners.abort();
    this.swipers.forEach((swiper) => swiper.destroy(true, true));
    this.paginators.forEach((paginator) => paginator.destroy());
    this.swipers = [];
    this.paginators = [];
  }

  dependenciesReady() {
    return typeof $ !== 'undefined' && typeof Swiper !== 'undefined' && typeof Ajaxinate !== 'undefined';
  }

  setup() {
    this.listeners = new AbortController();
    this.swipers = [];
    this.paginators = [];

    this.links = this.querySelectorAll('.materials-sidebar__link');
    this.panels = this.querySelectorAll('.materials__content');
    this.backdrop = this.querySelector('.material-modal-backdrop');

    this.bindTabs();
    this.querySelectorAll('.materials__grid').forEach((grid) => {
      this.initGrid(grid);
      this.paginateGrid(grid);
    });
    this.restoreFromUrl();

    // One document-level handler closes the open modal (cheaper than one per card).
    document.addEventListener('click', () => this.closeModals(), { signal: this.listeners.signal });

    if (window.Shopify && Shopify.designMode) this.bindEditorEvents();
  }

  // Selecting a block in the editor shows its group. Reorders/edits re-render the
  // section, which re-runs connectedCallback, so they need no handling here.
  bindEditorEvents() {
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const panel = this.querySelector(`[data-shopify-editor-block*="${event.detail.blockId}"]`);
        if (panel && panel.dataset.materialGroupContent) {
          this.activateGroup(panel.dataset.materialGroupContent, { animate: true });
        }
      },
      { signal: this.listeners.signal }
    );
  }

  bindTabs() {
    const { signal } = this.listeners;
    this.links.forEach((link) => {
      link.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.activateGroup(link.dataset.group, { updateHistory: true, animate: true });
        },
        { signal }
      );
    });

    window.addEventListener(
      'popstate',
      (event) => {
        const handle = (event.state && event.state.group) || this.handleFromHash();
        if (handle) this.activateGroup(handle, { animate: true });
      },
      { signal }
    );
  }

  activateGroup(handle, { updateHistory = false, animate = false } = {}) {
    let matched = false;

    this.links.forEach((link) => {
      const isActive = link.dataset.group === handle;
      link.classList.toggle('active', isActive);
      if (isActive) matched = true;
    });

    // Defer the layout-heavy panel switch a frame so the fade paints smoothly.
    requestAnimationFrame(() => {
      this.panels.forEach((panel) => {
        panel.classList.toggle('active', panel.dataset.materialGroupContent === handle);
      });
    });

    if (matched && updateHistory) history.pushState({ group: handle }, '', '#' + handle);
    return matched;
  }

  handleFromHash() {
    return decodeURIComponent((location.hash || '').replace(/^#/, ''));
  }

  hasGroup(handle) {
    return Array.from(this.links).some((link) => link.dataset.group === handle);
  }

  // Reveal the active group on load (hash match, else server default) without animating.
  restoreFromUrl() {
    const fromHash = this.handleFromHash();
    let handle = fromHash && this.hasGroup(fromHash) ? fromHash : '';
    if (!handle) {
      const activeLink = this.querySelector('.materials-sidebar__link.active');
      handle = activeLink ? activeLink.dataset.group : '';
    }
    if (handle) this.activateGroup(handle);
  }

  // Wire every not-yet-initialised card (on load and after each Ajaxinate page).
  initGrid(grid) {
    grid.querySelectorAll('.material').forEach((card) => this.initCard(card, grid));
  }

  // Paginate this grid independently; re-init Swiper on the appended cards.
  paginateGrid(grid) {
    const handle = grid.dataset.materialsGrid;
    this.paginators.push(
      new Ajaxinate({
        container: `[data-materials-grid="${handle}"]`,
        pagination: `[data-materials-pagination="${handle}"]`,
        method: 'click',
        loadingText: 'Loading',
        callback: () => {
          this.initGrid(grid);
          document.body.classList.remove('ajaxinate--loading');
        },
      })
    );
  }

  initCard(card, grid) {
    if (card.dataset.initialised === 'true') return;
    card.dataset.initialised = 'true';

    // Only cards with examples open a modal — skip the rest to avoid idle Swiper instances.
    if (card.dataset.hasExamples !== 'true') return;

    const { signal } = this.listeners;
    const modal = card.querySelector('.material-modal');
    const prevBtn = card.querySelector('.material-modal__action-btn--prev');
    const nextBtn = card.querySelector('.material-modal__action-btn--next');

    this.setPreviewThumbnails(card, grid, prevBtn, nextBtn);
    this.initCardSwiper(card);

    card.querySelector('.button__show-examples').addEventListener('click', (event) => this.openModal(event, grid, modal), { signal });
    card.querySelector('.material-modal__close').addEventListener('click', (event) => {
      event.preventDefault();
      this.closeModals();
    }, { signal });
    modal.addEventListener('click', (event) => event.stopPropagation(), { signal });
    prevBtn.addEventListener('click', (event) => this.step(event, card, grid, -1), { signal });
    nextBtn.addEventListener('click', (event) => this.step(event, card, grid, 1), { signal });
  }

  initCardSwiper(card) {
    const thumbs = new Swiper(card.querySelector('.swiper-container-thumbs'), {
      slidesPerView: 1,
      effect: 'fade',
    });

    const gallery = new Swiper(card.querySelector('.swiper-container'), {
      slidesPerView: 1,
      effect: 'fade',
      autoplay: { delay: 5000 },
      pagination: {
        el: card.querySelector('.material-modal__pagination'),
        clickable: true,
        renderBullet: (index, className) =>
          `<span class="material-modal__pagination-item ${className}">${index + 1}</span>`,
      },
      thumbs: { swiper: thumbs },
    });

    this.swipers.push(thumbs, gallery);
  }

  // Adjacent cards' images preview on the prev/next buttons (circular).
  setPreviewThumbnails(card, grid, prevBtn, nextBtn) {
    const cards = Array.from(grid.querySelectorAll('.material'));
    const index = cards.indexOf(card);
    const count = cards.length;
    prevBtn.querySelector('img').src = cards[(index - 1 + count) % count].querySelector('.material__img img').src;
    nextBtn.querySelector('img').src = cards[(index + 1) % count].querySelector('.material__img img').src;
  }

  openModal(event, grid, modal) {
    event.stopPropagation();

    // Clamp the scroll position to the section so the fixed modal stays in view.
    const top = window.scrollY + this.getBoundingClientRect().top;
    const bottom = top + this.offsetHeight - window.innerHeight;
    if (window.scrollY < top) window.scrollTo(0, top);
    else if (window.scrollY > bottom) window.scrollTo(0, bottom);

    this.classList.add('materials--modal-open');
    this.backdrop.classList.add('visible');
    grid.querySelectorAll('.material-modal').forEach((m) => m.classList.add('prepared'));
    modal.toggleAttribute('open', true);
    document.body.style.overflow = 'hidden';
  }

  closeModals() {
    this.classList.remove('materials--modal-open', 'materials--modal-navigating');
    this.querySelectorAll('.material-modal').forEach((m) => {
      m.removeAttribute('open');
      m.classList.remove('prepared');
    });
    this.backdrop.classList.remove('visible');
    document.body.style.overflow = 'auto';
  }

  // Switch to the next/previous card that has examples (wraps).
  step(event, card, grid, direction) {
    event.preventDefault();
    event.stopPropagation();
    const cards = Array.from(grid.querySelectorAll('.material'));
    const targetIndex = this.findCardWithExamples(cards, cards.indexOf(card), direction);
    if (targetIndex === null) return;

    const targetCard = cards[targetIndex];
    const currentModal = card.querySelector('.material-modal');
    const nextModal = targetCard.querySelector('.material-modal');
    if (currentModal === nextModal) return;

    this.classList.add('materials--modal-navigating');
    grid.querySelectorAll('.material-modal').forEach((m) => m.classList.add('prepared'));

    // Open incoming first so a modal is always visible (no grid flash).
    nextModal.setAttribute('open', '');
    if (currentModal !== nextModal) currentModal.removeAttribute('open');

    requestAnimationFrame(() => this.classList.remove('materials--modal-navigating'));

    this.setPreviewThumbnails(
      targetCard,
      grid,
      targetCard.querySelector('.material-modal__action-btn--prev'),
      targetCard.querySelector('.material-modal__action-btn--next')
    );
  }

  findCardWithExamples(cards, from, step) {
    let index = from + step;
    while (index !== from) {
      if (index >= cards.length) index = 0;
      if (index < 0) index = cards.length - 1;
      if (cards[index].dataset.hasExamples === 'true') return index;
      index += step;
    }
    return null;
  }
}

if (!customElements.get('materials-section')) {
  customElements.define('materials-section', MaterialsSection);
}
